import React, { useEffect, useState } from 'react';

import {
	RewardScene,
	RewardType,
	VoucherDetailInfo,
	VoucherType as VoucherTypeEnum
} from '@/types';
import { useReward } from '@/contexts/reward-context';
import { addCommas, replaceResource } from '@/utils';

import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CloseIcon,
	FireIcon,
	InfoIcon,
	LockIcon,
	SigninIcon,
	TimeIcon
} from '@/components/svg-icons';
import { CoinIcon } from '@/components/icons/coin-icon';
import { EventVoucherIcon } from '@/components/icons/event-voucher-icon';
import { ShopLayout } from '@/layouts/shop-layout';
import { SabaCoinEventContextProvider } from '@/contexts/saba-coin-context';
import { CoinEventContainer } from '@/features/coin-event/coin-event-container';
import { PromoteSbcModalContainer } from './components/promote-sbc-modal-container';
import { NavigationBar } from './components/navigation-bar';
import { RewardModalWrapper } from '@/components/reward-modal-wrapper';

type ShopTab = 'Cash' | 'Shop';

interface ShopTabItem {
	id: ShopTab;
	label: string;
}

export function SabaShop() {
	const {
		labels,
		platform,
		planInfo,
		hasReward,
		enableCoinEvent,
		enableCoinEventUpdate,
		setRewardScene,
		purchaseMessage,
		onClose,
		onKafkaSet,
		onStatementSabaCoinOpen
	} = useReward();

	let shopTabs: Array<ShopTabItem> = [
		{ id: 'Shop', label: 'lbl_RPRedeemVoucher' },
		{ id: 'Cash', label: 'lb_SBCLTRedemption' },
	];

	if (!enableCoinEvent) {
		shopTabs = shopTabs.filter(item => item.id !== 'Cash');
	}

	const [tab, setTab] = useState<ShopTab>(enableCoinEvent ? 'Cash' : 'Shop');

	const goToReward = () => {
		setRewardScene(RewardScene.Home);
	}

	const goToSignIn = () => {
		onKafkaSet({
			type: 4,
			action: 6
		});
		setRewardScene(RewardScene.SignInSpin);
	}

	const goToSabaCoinRecord = () => {
		onKafkaSet({
			type: 3,
			action: 11
		});
		onStatementSabaCoinOpen?.();
		onClose();
	}

	const handleChangeTab = (tab: ShopTab) => {
		let kafkaAction = 25;
		switch (tab) {
			case 'Shop':
				kafkaAction = 12;
				break;
		}
		onKafkaSet({
			type: 3,
			action: kafkaAction
		});
		setTab(tab);
	}

	return (
		<ShopLayout showTopBar={false}>
			<div className="reward-toast" data-show={!!purchaseMessage}>
				<div className="reward-toast__text">{purchaseMessage}</div>
			</div>
			<div className="shop-head">
				<div className="shop-top-bar">
					{hasReward ? (
						<div className="shop-top-bar__back" onClick={goToReward}>
							<ArrowLeftIcon />
						</div>
					) : (
						<div className="shop-top-bar__close" onClick={onClose}>
							<CloseIcon />
						</div>
					)}
				</div>

				{hasReward ? (
					<button
						className="reward-btn-side"
						type="button"
						onClick={goToSignIn}
					>
						<SigninIcon />
						<div className="reward-btn-side__text">{labels?.lbl_RPCheckIn}</div>
					</button>
				) : null}

				<div className="shop-head-info">
					<div className="shop-head-info__content">
						<div className="shop-head-info__title">
							{labels?.lbl_RPSabaShop}
						</div>
						<div className="shop-head-info__desc">
							{labels?.lbl_SBCInfo01}
						</div>
					</div>
					<div className="shop-head-info__coin-info">
						<div className="shop-head-info__coin">
							<div className="reward-coin">
								<CoinIcon />
								<div className="reward-coin__text">{addCommas(planInfo?.SabaCoin ?? 0)}</div>
								<div className="reward-coin__label">(SBC)</div>
							</div>
						</div>
						<div
							className="shop-head-info__histroy"
							onClick={goToSabaCoinRecord}
						>
							<div className="shop-head-info__histroy-text">{labels?.lbl_RPSabaCoinsRecords}</div>
							<ArrowRightIcon className="shop-head-info__histroy-icon" />
						</div>
					</div>
				</div>
			</div>
			<div className="shop-container">
				<NavigationBar />

				{hasReward ? (
					<div className="shop-tabs">
						{shopTabs.map(shopTab => (
							<div
								key={shopTab.id}
								className="shop-tab"
								data-selected={tab === shopTab.id}
								onClick={() => handleChangeTab(shopTab.id)}
							>
								<div className="shop-tab__title">{labels?.[shopTab.label]}</div>

								{
									shopTab.id === 'Cash' && (
										<div className="shop-tab__new">
											<img src="/events/reward/sbc-flow/sbc-flow-newtag.svg" alt="sbc-flow-newtag" />
										</div>
									)
								}

							</div>
						))}
					</div>
				) : null}

				{{
					'Shop': <ShopContent />,
					'Cash': (
						<SabaCoinEventContextProvider>
							<CoinEventContainer />
						</SabaCoinEventContextProvider>
					),
				}[tab]}
			</div>

			{enableCoinEventUpdate ? <PromoteSbcModalContainer /> : null}
		</ShopLayout >
	);
};

const ShopContent = () => {
	const { planInfo, labels, getVoucherStock, setRewardScene, rewardScene, onKafkaSet } = useReward();
	const [itemList, setItemList] = useState<Array<VoucherDetailInfo>>([]);

	const goToReward = () => {
		onKafkaSet({
			type: 3,
			action: 7
		})
		setRewardScene(RewardScene.Home);
	}

	const refreshVoucherStock = () => {
		if (getVoucherStock) {
			getVoucherStock().then((vouchers: VoucherDetailInfo[]) => {
				setItemList(vouchers);
			});
		}
	}

	useEffect(() => {
		refreshVoucherStock();
	}, [rewardScene]);

	const vipLevel = planInfo?.VipLevel ?? 0;

	return (
		<div className="shop-content" data-show="true">
			{
				vipLevel < 1 ?
					<div className="shop-content-head">
						<div className="shop-content-head__hint">
							<InfoIcon className="shop-content-head-hint-icon" />
							{replaceResource(labels?.lbl_RPUnlockVoucher as string, labels?.lbl_RPReward2 as string)}
						</div>
						<div
							className="shop-content-head__text-btn"
							onClick={goToReward}
						>
							{labels?.lbl_RPTask}
							<ArrowRightIcon className="shop-content-head-text-icon" />
						</div>
					</div> : null
			}
			<div className="shop-content-list">
				{
					itemList.map(item => {
						return (
							<ShopItem
								key={item.VoucherId}
								voucherDetailInfo={item}
							/>
						)
					})
				}
			</div>
		</div>
	);
};

interface ShopItemProps {
	voucherDetailInfo: VoucherDetailInfo
}

const ShopItem = ({ voucherDetailInfo }: ShopItemProps) => {
	const { labels, openVoucherConfirm, onKafkaSet, isInJackPotPrizeList, openRfbInfo } = useReward();
	const {
		VoucherDetailId,
		LimitPerPeriod,
		VoucherType,
		QuantitySold,
		QuotaAfterRate,
		PurchasedCountByUser,
		LimitPerUser,
		Price
	} = voucherDetailInfo;

	const isReachedLevel = !!VoucherDetailId;
	const reachPersonalLimit = LimitPerPeriod ? PurchasedCountByUser >= LimitPerUser && LimitPerUser != 0 && isReachedLevel : false;
	const hasStock = LimitPerPeriod ? QuantitySold < LimitPerPeriod : true;
	const soldOutPercentage = LimitPerPeriod ? Math.ceil((QuantitySold / LimitPerPeriod) * 100) : 0;
	const canPurchaseVipLevelName = {
		[VoucherTypeEnum.OddsBooster]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.RiskFreeBet]: labels?.lbl_RPReward3,
		[VoucherTypeEnum.CashExchange]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.NicknameChange]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.BetterCashExchange]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.BestCashExchange]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.NewRiskFreeBet]: labels?.lbl_RPReward3,
		[VoucherTypeEnum.StandardCashExchange]: labels?.lbl_RPReward2,
		[VoucherTypeEnum.GoodCashExchange]: labels?.lbl_RPReward2,
	}[VoucherType];

	if (!isInJackPotPrizeList && (
		VoucherType === VoucherTypeEnum.BetterCashExchange ||
		VoucherType === VoucherTypeEnum.BestCashExchange ||
		VoucherType === VoucherTypeEnum.StandardCashExchange ||
		VoucherType === VoucherTypeEnum.GoodCashExchange
	)) {
		return null;
	}

	const openConfirmPage = () => {
		onKafkaSet({
			type: 3,
			action: getKafkaAction()
		});
		if (openVoucherConfirm) {
			openVoucherConfirm(voucherDetailInfo);
		}
	};

	const getKafkaAction = () => {
		if (!isReachedLevel) {
			switch (VoucherType) {
				case VoucherTypeEnum.OddsBooster:
					return 8;
				case VoucherTypeEnum.RiskFreeBet:
				case VoucherTypeEnum.NewRiskFreeBet:
					return 9;
				case VoucherTypeEnum.CashExchange:
					return 10;
				default:
					return 30;
			}
		} else {
			switch (VoucherType) {
				case VoucherTypeEnum.OddsBooster:
					return 14;
				case VoucherTypeEnum.RiskFreeBet:
				case VoucherTypeEnum.NewRiskFreeBet:
					return 15;
				case VoucherTypeEnum.CashExchange:
					return 16;
				default:
					return 31;
			}
		}
	}

	const handlePurchase = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();

		let action;

		switch (VoucherType) {
			case VoucherTypeEnum.OddsBooster:
				action = 17;
				break;
			case VoucherTypeEnum.RiskFreeBet:
			case VoucherTypeEnum.NewRiskFreeBet:
				action = 18;
				break;
			case VoucherTypeEnum.CashExchange:
				action = 19;
				break;
			default:
				action = 32;
				break;
		}

		onKafkaSet({
			type: 3,
			action: action
		});

		if (openVoucherConfirm) {
			openVoucherConfirm(voucherDetailInfo);
		}
	}

	const showInfoModal = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		openRfbInfo?.();
	}

	return (
		<div className="shop-voucher" onClick={openConfirmPage}>
			<div className="shop-voucher-tag">
				{
					LimitPerPeriod ?
						<div className="shop-voucher-tag__text">
							<TimeIcon className="shop-voucher-tag-icon" />
							{labels?.lbl_RPWeeklyLimit}
						</div> : null
				}
			</div>
			<div className="shop-voucher-pack">
				<EventVoucherIcon voucherType={VoucherType} />
			</div>
			<div className="shop-voucher-info-content">
				<div className="shop-voucher-info">
					<div className="shop-voucher-info__title">{labels?.[voucherDetailInfo.Name]}</div>
					<div className="shop-voucher-info__desc">
						{
							!isReachedLevel ? (
								{
									[VoucherTypeEnum.OddsBooster]: labels?.lbl_RPOddsBoosterRule,
									[VoucherTypeEnum.RiskFreeBet]: labels?.lbl_RPRiskFreeBetRule,
									[VoucherTypeEnum.CashExchange]: labels?.lbl_RPExchangeCashRule,
									[VoucherTypeEnum.NicknameChange]: replaceResource(labels?.[voucherDetailInfo.Description] as string, `${QuotaAfterRate}`),
									[VoucherTypeEnum.BetterCashExchange]: labels?.lbl_RPExchangeCashRule,
									[VoucherTypeEnum.BestCashExchange]: labels?.lbl_RPExchangeCashRule,
									[VoucherTypeEnum.NewRiskFreeBet]: labels?.lbl_RPRiskFreeBetRule,
									[VoucherTypeEnum.StandardCashExchange]: labels?.lbl_RPExchangeCashRule,
									[VoucherTypeEnum.GoodCashExchange]: labels?.lbl_RPExchangeCashRule,
								}[VoucherType]
							) : replaceResource(labels?.[voucherDetailInfo.Description] as string, `${QuotaAfterRate}`)
						}
					</div>
					{
						// only limited voucher should shows progress bar
						LimitPerPeriod ?
							<div className="shop-voucher-progress">
								<div className="shop-voucher-progress__sold-range-text">
									{
										QuantitySold >= LimitPerPeriod ?
											labels?.lbl_RPSoldOut : replaceResource(labels?.lbl_RPSoldOutPercentage as string, `${soldOutPercentage}`)
									}
								</div>
								<div className="shop-voucher-progress__base">
									<div className="shop-voucher-progress__sold-range" style={{ width: `${soldOutPercentage}%` }} />
								</div>
							</div> : null
					}
					{
						!isReachedLevel ?
							<div className="shop-voucher-info__level-locked">
								<LockIcon className="shop-voucher-info-level-locked-icon" />
								<span>{replaceResource(labels?.lbl_RPUnlockAt as string, `${canPurchaseVipLevelName}`)}</span>
							</div> : null
					}
				</div>
				{
					isReachedLevel ?
						<div className="shop-voucher-info__price">
							<div className="reward-coin">
								<CoinIcon />
								<div className="reward-coin__text">{Price}</div>
							</div>
						</div> : null
				}
				{
					isReachedLevel && hasStock && !reachPersonalLimit ?
						<button
							className="reward-btn reward-btn--primary"
							type="button"
							onClick={(e) => handlePurchase(e)}
						>
							<div className="reward-btn__info">
								{
									LimitPerPeriod ?
										<FireIcon className="reward-btn__info-icon" /> : null
								}
								<span className="reward-btn__text">{LimitPerPeriod ? labels?.lbl_RPRedeem : labels?.lbl_RPExchange}</span>
							</div>
							<ArrowRightIcon />
						</button> : null
				}
				{
					reachPersonalLimit ?
						<button
							className="reward-btn reward-btn--sold"
							type="button"
							onClick={(e) => handlePurchase(e)}
						>
							<div className="reward-btn__info">
								<span className="reward-btn__text">{labels?.lbl_RPLimitReached}</span>
							</div>
						</button> : null
				}
				{
					!hasStock ?
						<button
							className="reward-btn reward-btn--sold"
							type="button"
							onClick={(e) => handlePurchase(e)}
						>
							<div className="reward-btn__info">
								<span className="reward-btn__text">{labels?.lbl_RPVoucherSoldOut}</span>
							</div>
						</button> : null
				}
			</div>
			{
				VoucherType === VoucherTypeEnum.NewRiskFreeBet ? (
					<div className="shop-voucher__info-btn" onClick={(e) => showInfoModal(e)}>
						<i className="vip-icon vip-icon--info"></i>{labels?.lbl_newRFB01}
					</div>
				) : null
			}
		</div>
	);
}